import { ICommentCommonListQueryParams, ICommentCommonPostBody, ICommentResponse } from '@sparelabs/comment-client'
import { GenericResource, IHttpAgent, IListResponse, OrderDirection } from '@sparelabs/http-client-utils'
import {
  IDutiesPostBody,
  IDutiesSearchQueryParams,
  IDutiesSearchResponse,
  IDutyBulkCancelPostBody,
  IDutyBulkCopyPostBody,
  IDutyBulkDeletePostBody,
  IDutyCancellationPostBody,
  IDutyExportData,
  IDutyExportQueryParams,
  IDutyHistoryResponse,
  IDutyPatchBody,
  IDutyPolylineQueryParams,
  IDutyPolylineResponse,
  IDutyPostBody,
  IDutyQueryParams,
  IDutyResponse,
  IDutyScheduleDutiesQueryParams,
  IDutyScheduleDutyResponse,
  IDutyScheduleRequestResponse,
  IDutyScheduleRequestsQueryParams,
  IDutyStubQueryParams,
  IDutyStubResponse,
  ILastVehicleLocationResponse,
  IVehicleLocationPostBody,
  Resources,
} from '../types'

export class DutyResource extends GenericResource<IDutyResponse, IDutyQueryParams, IDutyPatchBody, IDutyPostBody> {
  public post = this.buildPost()
  public patch = this.buildPatch()
  public del = this.buildDel()
  public get = this.buildGet()
  public list = this.buildList()

  constructor(agent: IHttpAgent) {
    super(Resources.Duties, agent)
  }

  public async getLastVehicleLocation(dutyId: string): Promise<ILastVehicleLocationResponse> {
    const res = await this.agent.get<ILastVehicleLocationResponse>(`${Resources.Duties}/${dutyId}/vehicleLocation`)
    return res.data
  }

  public async postVehicleLocation(
    dutyId: string,
    body: IVehicleLocationPostBody,
  ): Promise<ILastVehicleLocationResponse> {
    const res = await this.agent.post<ILastVehicleLocationResponse>(
      `${Resources.Duties}/${dutyId}/vehicleLocation`,
      body,
    )
    return res.data
  }

  public async cancel(dutyId: string, body: IDutyCancellationPostBody): Promise<IDutyResponse> {
    const res = await this.agent.post<IDutyResponse>(`${Resources.Duties}/${dutyId}/cancellation`, body)
    return res.data
  }

  public async getPolyline(dutyId: string, params: IDutyPolylineQueryParams = {}): Promise<IDutyPolylineResponse> {
    const res = await this.agent.get<IDutyPolylineResponse>(`${Resources.Duties}/${dutyId}/polyline`, { params })
    return res.data
  }

  public async getStub(dutyId: string): Promise<IDutyStubResponse> {
    const res = await this.agent.get<IDutyStubResponse>(`${Resources.Duties}/${dutyId}/stub`)
    return res.data
  }

  public async listDutySchedulesDuties(
    params: IDutyScheduleDutiesQueryParams,
    fetchAllPages: boolean = false,
  ): Promise<Omit<IListResponse<IDutyScheduleDutyResponse>, 'total'>> {
    if (fetchAllPages) {
      const data = await this.fetchAll(`${Resources.DutySchedules}/duties`, params)
      return { data, skip: 0, limit: data.length }
    }

    const res = await this.agent.get<Omit<IListResponse<IDutyScheduleDutyResponse>, 'total'>>(
      `${Resources.DutySchedules}/duties`,
      { params },
    )
    return res.data
  }

  public async listDutySchedulesRequests(
    params: IDutyScheduleRequestsQueryParams,
    fetchAllPages: boolean = false,
  ): Promise<Omit<IListResponse<IDutyScheduleRequestResponse>, 'total'>> {
    if (fetchAllPages) {
      const data = await this.fetchAllRequests(`${Resources.DutySchedules}/requests`, params)
      return { data, skip: 0, limit: data.length }
    }

    const res = await this.agent.get<Omit<IListResponse<IDutyScheduleRequestResponse>, 'total'>>(
      `${Resources.DutySchedules}/requests`,
      { params },
    )
    return res.data
  }

  public async listStubs(
    params: IDutyStubQueryParams,
    fetchAllPages: boolean = false,
  ): Promise<IListResponse<IDutyStubResponse>> {
    if (fetchAllPages) {
      const data = await this.allPageFetcher.fetch<IDutyStubResponse, IDutyStubQueryParams>('dutyStubs', params)
      return { data, skip: 0, total: data.length, limit: data.length }
    }

    const res = await this.agent.get<IListResponse<IDutyStubResponse>>('dutyStubs', { params })
    return res.data
  }

  public async postBulk(body: IDutiesPostBody): Promise<IListResponse<IDutyResponse>> {
    const res = await this.agent.post<IListResponse<IDutyResponse>>(`${Resources.Duties}/bulk`, body)
    return res.data
  }

  public async postBulkCancel(body: IDutyBulkCancelPostBody): Promise<{ count: number }> {
    const res = await this.agent.post<{ count: number }>(`${Resources.Duties}/bulkCancel`, body)
    return res.data
  }

  public async postBulkCopy(body: IDutyBulkCopyPostBody): Promise<{ count: number }> {
    const res = await this.agent.post<{ count: number }>(`${Resources.Duties}/bulkCopy`, body)
    return res.data
  }

  public async postBulkDelete(body: IDutyBulkDeletePostBody): Promise<{ count: number }> {
    const res = await this.agent.post<{ count: number }>(`${Resources.Duties}/bulkDelete`, body)
    return res.data
  }

  public async getHistory(dutyId: string): Promise<IDutyHistoryResponse> {
    const res = await this.agent.get<IDutyHistoryResponse>(`${Resources.Duties}/${dutyId}/history`)
    return res.data
  }

  public async search(params: IDutiesSearchQueryParams): Promise<IDutiesSearchResponse> {
    const res = await this.agent.get<IDutiesSearchResponse>('duties/search', { params })
    return res.data
  }

  /**
   * Fetches all pages of a list resource without relying on a total count to end.
   */
  public async fetchAll(path: string, params: IDutyScheduleDutiesQueryParams): Promise<IDutyScheduleDutyResponse[]> {
    const duties: IDutyScheduleDutyResponse[] = []

    while (true) {
      const res = await this.agent.get<Omit<IListResponse<IDutyScheduleDutyResponse>, 'total'>>(path, {
        params: {
          orderBy: 'createdAt',
          orderDirection: OrderDirection.Ascending,
          ...params,
          skip: duties.length,
        },
      })

      const listResponse = res.data
      duties.push(...res.data.data)

      if (listResponse.data.length < listResponse.limit || listResponse.data.length === 0) {
        return duties
      }
    }
  }

  public async fetchAllRequests(
    path: string,
    params: IDutyScheduleRequestsQueryParams,
  ): Promise<IDutyScheduleRequestResponse[]> {
    const requests: IDutyScheduleRequestResponse[] = []

    while (true) {
      const res = await this.agent.get<Omit<IListResponse<IDutyScheduleRequestResponse>, 'total'>>(path, {
        params: {
          orderBy: 'createdAt',
          orderDirection: OrderDirection.Ascending,
          ...params,
          skip: requests.length,
        },
      })

      const listResponse = res.data
      requests.push(...res.data.data)

      if (listResponse.data.length < listResponse.limit || listResponse.data.length === 0) {
        return requests
      }
    }
  }

  public async postDutyComment(dutyId: string, body: ICommentCommonPostBody): Promise<ICommentResponse> {
    const res = await this.agent.post<ICommentResponse>(`${Resources.Duties}/${dutyId}/comments`, body)
    return res.data
  }

  public async listDutyComments(
    dutyId: string,
    params: ICommentCommonListQueryParams,
  ): Promise<{ data: ICommentResponse[] }> {
    const res = await this.agent.get<{ data: ICommentResponse[] }>(`${Resources.Duties}/${dutyId}/comments`, {
      params,
    })

    return res.data
  }

  public async export(params: IDutyExportQueryParams): Promise<IListResponse<IDutyExportData>> {
    const res = await this.agent.get<IListResponse<IDutyExportData>>(`${Resources.Duties}/export`, { params })
    return res.data
  }
}
